/**
 * HUD layout store — persisted per-control offset / scale / opacity for the sea HUD ("HUD Forge").
 * 100% client-side & cosmetic: nothing here touches the wire, the sim or the server.
 *
 * The DEFAULT is an EMPTY map: an element with no entry gets NO inline style at all, so a player who
 * never opens the editor sees the shipped HUD byte-for-byte. Offsets are stored as FRACTIONS of the
 * viewport (dx of width, dy of height) so a saved layout survives rotation and device swaps; scale and
 * opacity ride on the same entry. Everything is applied as a composed CSS transform + opacity on top of
 * the shipped stylesheet — the shipped rules are never edited, so removing an entry restores the default.
 *
 * Persisted as versioned JSON in localStorage['sor:hudlayout']. Migration is tolerant (unknown keys are
 * dropped, out-of-range values clamped, a corrupt blob yields the default); the pure helpers below carry
 * no DOM/localStorage so they run under the node test environment.
 */

/** One control's saved layout: viewport-fraction offsets, scale multiplier, opacity multiplier. */
export interface ElemLayout { dx: number; dy: number; s: number; o: number }
export type HudLayout = Record<string, ElemLayout>;

export interface HudElemDef {
  /** Stable storage key (never rename — saved layouts reference it). */
  key: string;
  /** DOM selector for the live element. */
  sel: string;
  label: string;
  icon: string;
  /** Shipped CSS transform this element relies on (composed BEFORE ours), e.g. the speedo's centering. */
  baseT?: string;
  /** The element animates its own opacity (e.g. the aim gauge fade) — the editor must not fight it. */
  noOpacity?: boolean;
  /** Included in the Southpaw (mirrored) preset flip. */
  mirror?: boolean;
  /** Editor-only: CSS display value to force the element visible while editing (via !important —
   *  the game loop rewrites inline styles every frame, so plain inline forcing loses the fight). */
  show?: string;
  /** Editor-only: the element hides via opacity (the aim gauge) — force opacity:1 while editing. */
  showOpacity?: boolean;
}

/**
 * Every editable sea-HUD control. Grouped the way thumbs use them: the helm, the fire pair and the
 * utility 2×2 move as units (they are flex groups — splitting them would orphan the shipped layout).
 */
export const HUD_ELEMS: readonly HudElemDef[] = [
  { key: 'steer', sel: '#steer', label: 'Helm', icon: '☸', mirror: true },
  { key: 'firerow', sel: '#firerow', label: 'Fire buttons', icon: '🔥', mirror: true },
  { key: 'utilgrid', sel: '#utilgrid', label: 'Sail · Anchor · Bail', icon: '⚓', mirror: true },
  { key: 'speedo', sel: '#speedo', label: 'Speed gauge', icon: '🧭', baseT: 'translateX(-50%) ' },
  { key: 'elevwrap', sel: '#elevwrap', label: 'Aim gauge', icon: '🎯', mirror: true, noOpacity: true, showOpacity: true },
  { key: 'buffbtn', sel: '#buffbtn', label: 'Tonics', icon: '⚗', mirror: true, show: 'flex' },
  { key: 'topbar', sel: '#topbar', label: 'Status card', icon: '⛨' },
  { key: 'mapwrap', sel: '#mapwrap', label: 'Chart', icon: '🗺' },
  /* leaderboard / contracts / journal keys retired — those panels left the sea HUD for the Book's Ship's
     Log (owner order). coerceHudLayout drops their saved entries harmlessly. */
  { key: 'bktoggle', sel: '#bktoggle', label: 'Book button', icon: '📖' },
  { key: 'mutebtn', sel: '#mutebtn', label: 'Mute button', icon: '🔊' },
  { key: 'invbtn', sel: '#invbtn', label: 'Stores button', icon: '🎒' },
  { key: 'chatbtn', sel: '.cc-chat-toggle', label: 'Chat button', icon: '💬' },
];

export const HUDLAYOUT_VERSION = 1;
const STORAGE_KEY = 'sor:hudlayout';

/** Hard clamps — mirror what the editor's sliders offer, with a little headroom. */
export const HL = { DXY_MAX: 1.2, S_MIN: 0.5, S_MAX: 1.8, O_MIN: 0.25 } as const;

export const IDENTITY_ELEM: ElemLayout = { dx: 0, dy: 0, s: 1, o: 1 };

/* ------------------------------- pure helpers (side-effect free) ------------------------------- */

const clampF = (n: unknown, lo: number, hi: number, fallback: number): number => {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : NaN;
  if (Number.isNaN(v)) return fallback;
  return v < lo ? lo : v > hi ? hi : v;
};

/** True when an entry is (within epsilon) the shipped default — such entries are dropped on save. */
export function isIdentityElem(e: ElemLayout): boolean {
  return Math.abs(e.dx) < 5e-4 && Math.abs(e.dy) < 5e-4 && Math.abs(e.s - 1) < 5e-3 && e.o > 0.995;
}

/** Clamp one raw entry into a valid {@link ElemLayout}. */
export function coerceElem(raw: unknown): ElemLayout {
  const r = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
  return {
    dx: clampF(r.dx, -HL.DXY_MAX, HL.DXY_MAX, 0),
    dy: clampF(r.dy, -HL.DXY_MAX, HL.DXY_MAX, 0),
    s: clampF(r.s, HL.S_MIN, HL.S_MAX, 1),
    o: clampF(r.o, HL.O_MIN, 1, 1),
  };
}

/**
 * Fold an arbitrary parsed blob into a valid {@link HudLayout}: only known element keys survive, every
 * entry is clamped, and identity entries are dropped (so "moved back to default" == "never touched").
 */
export function coerceHudLayout(raw: unknown): HudLayout {
  const r = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
  const out: HudLayout = {};
  for (const def of HUD_ELEMS) {
    if (!(def.key in r)) continue;
    const e = coerceElem(r[def.key]);
    if (!isIdentityElem(e)) out[def.key] = e;
  }
  return out;
}

/** Custom layouts are stored PER ORIENTATION: an offset saved as a fraction of a portrait viewport
 *  means something completely different sideways (owner's landscape screenshot proved it). `p` =
 *  portrait, `l` = landscape (desktop always lives in `l`). */
export interface HudLayoutStore { p: HudLayout; l: HudLayout }

/** Parse a stored JSON string (or null) into a valid store; any error yields the default (empty).
 *  A v1 blob written before orientation buckets existed migrates its `elems` into portrait. */
export function parseHudLayout(json: string | null | undefined): HudLayoutStore {
  if (!json) return { p: {}, l: {} };
  try {
    const blob = JSON.parse(json) as { v?: number; elems?: unknown; elemsL?: unknown };
    return { p: coerceHudLayout(blob?.elems), l: coerceHudLayout(blob?.elemsL) };
  } catch { return { p: {}, l: {} }; }
}

export function serializeHudLayout(s: HudLayoutStore): string {
  return JSON.stringify({ v: HUDLAYOUT_VERSION, elems: s.p, elemsL: s.l });
}

/**
 * The dx that mirrors an element across the vertical screen axis: with the element's DEFAULT center at
 * `baseCenterX` px on a `vw`-wide screen, applying this dx puts its center at the mirrored position.
 * Used by the Southpaw preset (left-handed players want fire under the left thumb, helm under the right).
 */
export function mirroredDx(baseCenterX: number, vw: number): number {
  if (!(vw > 0)) return 0;
  const dx = (vw - 2 * baseCenterX) / vw;
  return dx < -HL.DXY_MAX ? -HL.DXY_MAX : dx > HL.DXY_MAX ? HL.DXY_MAX : dx;
}

/* --------------------------------- the store (stateful) --------------------------------- */

function readStorage(): HudLayoutStore {
  try {
    if (typeof localStorage === 'undefined') return { p: {}, l: {} };
    return parseHudLayout(localStorage.getItem(STORAGE_KEY));
  } catch { return { p: {}, l: {} }; }
}

let current: HudLayoutStore = readStorage();

/** The active orientation bucket ('l' also covers desktop — always-landscape viewports). */
function bucket(): keyof HudLayoutStore {
  try { return window.matchMedia?.('(orientation: landscape)').matches ? 'l' : 'p'; } catch { return 'p'; }
}

/** A defensive deep copy of the saved layout for the CURRENT orientation. */
export function getHudLayout(): HudLayout {
  const cur = current[bucket()];
  const out: HudLayout = {};
  for (const k of Object.keys(cur)) out[k] = { ...cur[k]! };
  return out;
}

/** Validate + persist the CURRENT orientation's layout (the other bucket is preserved), re-apply. */
export function setHudLayout(l: HudLayout): void {
  current[bucket()] = coerceHudLayout(l);
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, serializeHudLayout(current)); }
  catch { /* private mode / quota — the layout still lives in memory for this session */ }
  applyHudLayout();
}

/* --------------------------------- DOM application --------------------------------- */

function setElemTransform(el: HTMLElement, def: HudElemDef, e: ElemLayout, vw: number, vh: number, fx = 0, fy = 0): void {
  const tx = e.dx * vw + fx, ty = e.dy * vh + fy;
  el.style.transform = `${def.baseT ?? ''}translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) scale(${e.s})`;
}

/**
 * Apply a layout (default: the saved one) to the live HUD. Elements WITHOUT an entry get their inline
 * style cleared — the shipped stylesheet takes back over. After applying, each styled element is nudged
 * back on-screen if the current viewport would clip it (rotation / a smaller device than it was saved on).
 * Cheap (runs on boot, resize and while editing — never per frame) and idempotent.
 */
export function applyHudLayout(layout?: HudLayout): void {
  if (typeof document === 'undefined') return;
  if (!layout) layout = current[bucket()]; // rotation re-applies through the resize listener → bucket swaps

  const vw = window.innerWidth, vh = window.innerHeight;
  for (const def of HUD_ELEMS) {
    const el = document.querySelector(def.sel) as HTMLElement | null;
    if (!el) continue;
    const e = layout[def.key];
    if (!e) {
      if (el.dataset.sorHl) { delete el.dataset.sorHl; el.style.transform = ''; el.style.opacity = ''; }
      continue;
    }
    el.dataset.sorHl = '1';
    setElemTransform(el, def, e, vw, vh);
    if (!def.noOpacity) el.style.opacity = e.o > 0.995 ? '' : String(e.o);
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) { // hidden elements measure 0×0 — clamp when they show via resize/edit
      const M = 4;
      let fx = 0, fy = 0;
      if (r.left < M) fx = M - r.left; else if (r.right > vw - M) fx = (vw - M) - r.right;
      if (r.top < M) fy = M - r.top; else if (r.bottom > vh - M) fy = (vh - M) - r.bottom;
      if (fx !== 0 || fy !== 0) setElemTransform(el, def, e, vw, vh, fx, fy);
    }
  }
}

let autoBound = false;
/** Apply the saved layout now and keep it applied across resizes/rotation. Idempotent. */
export function bindHudLayoutAutoApply(): void {
  applyHudLayout();
  if (autoBound || typeof window === 'undefined') return;
  autoBound = true;
  let raf = 0;
  const onResize = (): void => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => applyHudLayout()); };
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
}

/** TEST-ONLY: reset the in-memory store (does not touch localStorage). */
export function __resetHudLayoutForTest(): void { current = { p: {}, l: {} }; }
